import { INDUSTRIES } from "./industryCatalog";

export const SITE_URL = "https://gorillaworksmarketing.com";
export const DEFAULT_IMAGE = `${SITE_URL}/opengraph.jpg`;

export type Metadata = {
  title: string;
  description: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
};

export const publicPages: Record<string, Metadata> = {
  "/": { title: "Direct Mail Marketing That Drives Growth | Gorilla Works", description: "Gorilla Works helps local businesses plan, print, and measure direct mail campaigns alongside web and digital marketing.", schema: [{ "@context": "https://schema.org", "@type": "Organization", name: "Gorilla Works Marketing", url: SITE_URL, logo: `${SITE_URL}/logo.webp` }, { "@context": "https://schema.org", "@type": "WebSite", name: "Gorilla Works Marketing", url: SITE_URL }] },
  "/about": { title: "About Gorilla Works Marketing", description: "Learn about Gorilla Works Marketing and how we help local businesses connect direct mail, websites, and digital marketing." },
  "/contact": { title: "Contact Gorilla Works Marketing", description: "Contact Gorilla Works Marketing to discuss direct mail, website development, SEO, and digital advertising for your business." },
  "/custom-direct-mail": {
    title: "Custom Direct Mail Marketing | Gorilla Works",
    description: "Create personalized, automated direct mail campaigns with CRM integrations, location-aware targeting, unique QR codes, and measurable attribution.",
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Custom Direct Mail Marketing",
      provider: { "@type": "Organization", name: "Gorilla Works Marketing", url: SITE_URL },
       url: `${SITE_URL}/custom-direct-mail/`,
    },
  },
  "/industries": { title: "Direct Mail Marketing by Industry | Gorilla Works", description: "Explore direct mail marketing strategies for home services, local businesses, and industry-specific growth campaigns." },
  "/print-shop": { title: "Direct Mail Print Shop | Gorilla Works", description: "Explore Gorilla Works print-shop options for business postcards, sheets, and campaign-ready direct mail." },
  "/print-shop/postcards": { title: "Business Postcard Printing | Gorilla Works", description: "Create memorable business postcards for local direct mail campaigns with Gorilla Works marketing and printing services." },
  "/print-shop/sheets": { title: "Direct Mail Sheet Printing | Gorilla Works", description: "Print campaign sheets and direct mail collateral designed to help local businesses communicate clearly with customers." },
  "/pricing": { title: "Custom Direct Mail Pricing | Gorilla Works", description: "3,000 custom mailers for $2,970/month, 6,000 for $5,700, or 10,000 for $9,100. Statistical audience selection, design, fulfillment and reporting." },
  "/integrations": { title: "Marketing Integrations | Gorilla Works", description: "See how Gorilla Works integrations help connect marketing activity, business data, and campaign measurement." },
  "/quote": { title: "Request a Marketing Quote | Gorilla Works", description: "Request a custom quote for direct mail, print, website, SEO, or digital advertising services from Gorilla Works." },
  "/book-demo": { title: "Book a Gorilla Works Demo", description: "Book a demo to see how Gorilla Works can support measurable direct mail and local marketing campaigns." },
  "/calculator": { title: "Custom Direct Mail Calculator | Gorilla Works", description: "Estimate custom mail totals and per-piece costs with professional design, statistical audience selection and individual QR response tracking." },
  "/map-intel": { title: "Map Intel: Carrier Route Demographics Map | Gorilla Works", description: "Explore USPS carrier routes with Census demographics, home and building data. Search a ZIP or route ID and filter routes for your next direct mail campaign." },
  "/causal-analytics": { title: "Marketing Causal Analytics | Gorilla Works", description: "Learn how Gorilla Works uses causal analytics to help businesses understand the impact of their marketing campaigns." },
  "/services/direct-mail": { title: "Direct Mail Marketing Services | Gorilla Works", description: "Plan, target, print, and measure direct mail marketing campaigns for your local business with Gorilla Works." },
  "/services/website-development": { title: "Website Development Services | Gorilla Works", description: "Build a business website designed to clearly present your services and support your wider marketing efforts." },
  "/services/seo": { title: "SEO Services for Local Businesses | Gorilla Works", description: "Improve your local business website's search visibility with practical SEO services from Gorilla Works." },
  "/services/google-meta-ads": { title: "Google & Meta Ads Management | Gorilla Works", description: "Reach prospective customers with Google and Meta advertising services integrated with your local marketing strategy." },
  "/privacy": { title: "Privacy Policy | Gorilla Works", description: "Read the Gorilla Works Marketing privacy policy." },
  "/data-deletion": { title: "Data Deletion Request | Gorilla Works", description: "Request deletion of personal data held by Gorilla Works Marketing." },
  "/terms": { title: "Terms of Service | Gorilla Works", description: "Read the Gorilla Works Marketing terms of service." },
  "/legal": { title: "Government & Law Enforcement Request Policy | Gorilla Works", description: "Read how Gorilla Works Marketing reviews and responds to government and law enforcement requests for user data." },
};

const industries: Record<string, string> = {
  roofing: "Roofing", "garage-doors": "Garage Door", hvac: "HVAC", "exterior-cleaning": "Exterior Cleaning",
  "pest-control": "Pest Control", "holiday-lighting": "Holiday Lighting", "lawn-care": "Lawn Care", "tree-care": "Tree Care",
  "carpet-cleaning": "Carpet Cleaning", construction: "Construction", landscaping: "Landscaping", "window-cleaning": "Window Cleaning",
  "garage-floor-coating": "Garage Floor Coating", "power-washing": "Power Washing", decks: "Deck", painting: "Painting",
  plumbing: "Plumbing", fencing: "Fencing", electrical: "Electrical", restaurants: "Restaurant", farms: "Farm & Agricultural Supply",
  "churches-nonprofits": "Church & Nonprofit", "e-commerce": "E-Commerce", "general-contractors": "General Contractor",
  retail: "Retail", apartments: "Apartment",
  telecommunications: "Telecommunications", "personal-protective-equipment": "Personal Protective Equipment",
  "janitorial-sanitation": "Janitorial & Sanitation", "industrial-manufacturing-supplies": "Industrial & Manufacturing",
  "financial-services": "Financial Services", "medical-dental-mental-health": "Medical & Mental Health",
  "veterinary-services": "Veterinary Services",
};

for (const [slug, name] of Object.entries(industries)) {
  const path = `/industries/${slug}`;
  publicPages[path] = {
    title: `${name} Direct Mail Marketing | Gorilla Works`,
    description: `Explore direct mail marketing ideas and campaign support for ${name.toLowerCase()} businesses from Gorilla Works.`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${name} Direct Mail Marketing`,
      provider: { "@type": "Organization", name: "Gorilla Works Marketing", url: SITE_URL },
       url: `${SITE_URL}${path}/`,
    },
  };
}

export function normalizeRoutePath(value: string): string {
  const path = value.split(/[?#]/, 1)[0].replace(/\/+$/, "");
  return path || "/";
}

/** Static public/campaign directories use the edge's final slash shape. Private routes and
 * API-owned blog documents are deliberately not rewritten by this allowlist. */
export function publicHref(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//")) return value;
  const path = normalizeRoutePath(value);
  if ((!Object.hasOwn(publicPages, path) && !Object.hasOwn(campaignPages, path)) || path === "/") return value;
  return `${path}/${value.slice(value.split(/[?#]/, 1)[0].length)}`;
}

// Campaigns are public landing pages, explicitly noindex and never in the sitemap.
export const campaignPages: Record<string, Metadata> = {
  "/demo": {
    title: "See Direct Mail Built for Your Business | Gorilla Works",
    description: "Book a direct-mail demo to explore audiences, creative, delivery and campaign measurement with Gorilla Works.",
  },
  "/demo/first-mail-drop-offer": {
    title: "Up to $500 off your first mail drop | Gorilla Works",
    description: "Up to $500 off your first mail drop. Book a demo today to discuss your direct-mail campaign with Gorilla Works.",
  },
  ...Object.fromEntries(INDUSTRIES.map(industry => [
    `/demo/industries/${industry.slug}`,
    {
      title: `${industry.name} Direct-Mail Demo | Gorilla Works`,
      description: `Book a demo to explore direct-mail campaigns for ${industry.name.toLowerCase()}. ${industry.desc}`,
    },
  ])),
};

export function getRouteMetadata(value: string) {
  const path = normalizeRoutePath(value);
  const metadata = publicPages[path] ?? campaignPages[path];
   const canonical = `${SITE_URL}${publicHref(path)}`;
  const noindex = !publicPages[path];
  const title = metadata?.title ?? "Private Page | Gorilla Works";
  const description = metadata?.description ?? "This Gorilla Works page is not available in public search results.";
  const schema = metadata && !noindex
    ? [
        ...(Array.isArray(metadata.schema) ? metadata.schema : metadata.schema ? [metadata.schema] : []),
        ...(path === "/" ? [] : [{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: metadata.title.replace(" | Gorilla Works", ""), item: canonical },
          ],
        }]),
      ]
    : undefined;

  return { path, metadata, canonical, noindex, title, description, schema };
}

function escapeAttribute(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

export function renderMetadataHtml(value: string): string {
  const { canonical, description, noindex, schema, title } = getRouteMetadata(value);
  const attr = escapeAttribute;
  const tags = [
    `<title>${title.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")}</title>`,
    `<meta name="description" content="${attr(description)}" />`,
    `<meta name="robots" content="${noindex ? "noindex, nofollow" : "index, follow"}" />`,
    `<link rel="canonical" href="${attr(canonical)}" />`,
    `<meta property="og:title" content="${attr(title)}" />`,
    `<meta property="og:description" content="${attr(description)}" />`,
    `<meta property="og:url" content="${attr(canonical)}" />`,
    `<meta property="og:image" content="${attr(DEFAULT_IMAGE)}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${attr(title)}" />`,
    `<meta name="twitter:description" content="${attr(description)}" />`,
    `<meta name="twitter:image" content="${attr(DEFAULT_IMAGE)}" />`,
  ];
  if (schema) {
    tags.push(`<script id="route-json-ld" type="application/ld+json">${JSON.stringify(schema).replaceAll("<", "\\u003c")}</script>`);
  }
  return tags.join("\n    ");
}
